// deps
import React, { ReactElement, memo, useState, useCallback, useRef, useMemo, useEffect } from "react";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// interfaces
import { CardPaymentMethodData } from "@payment-ui/interfaces/Card";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// components
import Cvv from "./Cvv";

// helpers
import { getCardMethodTestIds, getValidCardCvvLength } from "@payment-ui/helpers/Card";
import { validateCVV } from "@payment-ui/helpers/Cvv";

import styles from "../Card.v4.scss";

interface PayButtonProps {
    enabled: boolean;
    methodData: CardPaymentMethodData;
    onPay: (cvv: string) => void;
    paymentAmount: string;
    cvvRequired: boolean;
    payCtaText?: string;
    payCtaClassName?: string;
    onHideCvv?: VoidFunction;
}

function PayButton(props: PayButtonProps): ReactElement {
    const cvv = useRef("");
    const _payButtonContainer = useRef<HTMLDivElement | null>(null);
    const [validCvv, setValidCvv] = useState(true);
    const { onPay, methodData, cvvRequired = true, onHideCvv, enabled } = props;

    const validCvvLength = useMemo(() => getValidCardCvvLength(methodData), [methodData]);

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.stopPropagation();

            if (validateCVV(methodData, cvv.current, cvvRequired)) {
                setValidCvv(true);
                onPay(cvv.current);
                return;
            }

            setValidCvv(false);
        },
        [methodData, onPay, cvvRequired],
    );

    const onCvvChange = useCallback(
        (value: string): void => {
            cvv.current = value;
            if (!validCvv) {
                setValidCvv(true);
            }
        },
        [validCvv],
    );

    const testIds = useMemo(() => getCardMethodTestIds(methodData), [methodData]);

    useEffect(() => {
        if (!cvvRequired && typeof onHideCvv === "function") {
            onHideCvv();
        }
    }, [cvvRequired, onHideCvv]);

    return (
        <div className={styles.paymentMethodCardPay} ref={_payButtonContainer}>
            <div className={styles.paymentMethodCardPayContent}>
                {cvvRequired && (
                    <Cvv
                        valid={validCvv}
                        onChange={onCvvChange}
                        autoFocus
                        disabled={!enabled}
                        data-testid={testIds.CVV}
                        validCvvLength={validCvvLength}
                    />
                )}
                <div className={styles.paymentMethodCardPayButton}>
                    <SuperButton
                        variant="fullwidth"
                        buttonType="specialPositive"
                        buttonSize="medium"
                        isDisabled={!enabled}
                        onClick={onClick}
                        data-testid={testIds.PAY_BUTTON}
                        className={props.payCtaClassName}
                    >
                        <span>{props.payCtaText ?? TEXTS.PAY}</span>
                        <span className={styles.paymentMethodCardPayButtonAmount}>{props.paymentAmount}</span>
                    </SuperButton>
                </div>
            </div>
        </div>
    );
}

export default memo(PayButton);
